import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { track } from '../utils/analytics';

const MAX_SIZE_MB = 5;
const ACCEPTED    = ['image/jpeg', 'image/png', 'image/webp'];

export default function Onboarding() {
  const { user, refreshProfile } = useAuth();
  const navigate  = useNavigate();
  const inputRef  = useRef();

  const [preview,  setPreview]  = useState(null);
  const [file,     setFile]     = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  function handleFile(f) {
    setError('');
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      setError('JPG, PNG, or WEBP only.');
      return;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Max ${MAX_SIZE_MB}MB. Please compress the file.`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  async function handleUpload() {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const ext  = file.name.split('.').pop();
      const path = `${user.id}/profile.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(path);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_photo_url: publicUrl, photo_approved: false })
        .eq('id', user.id);
      if (updateError) throw updateError;

      track('photo_uploaded');
      await refreshProfile();
      navigate('/feed');
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div className="container">
        <div className="card">
          <h2 style={{ color: 'var(--gold)', marginBottom: 6 }}>
            Upload your photo
          </h2>
          <p className="text-muted mb-24" style={{ fontSize: '0.9rem' }}>
            One photo. Your best one, or your most chaotic one.
            <br />
            <span style={{ fontSize: '0.8rem' }}>Everyone here judges. That is the whole point.</span>
          </p>

          <div
            className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED.join(',')}
              onChange={e => handleFile(e.target.files[0])}
            />
            {preview ? (
              <img src={preview} alt="Your upload preview" />
            ) : (
              <div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  Click to select or drag and drop
                </p>
                <p style={{ color: 'var(--mid-gray)', fontSize: '0.75rem', marginTop: 6 }}>
                  JPG, PNG, WEBP — max 5MB
                </p>
              </div>
            )}
          </div>

          {preview && (
            <button
              className="btn-ghost mt-8"
              onClick={() => { setPreview(null); setFile(null); }}
              style={{ width: '100%', textAlign: 'center', fontSize: '0.8rem' }}
            >
              Choose a different photo
            </button>
          )}

          {error && <p className="form-error mt-16">{error}</p>}

          <div className="mt-24">
            <button
              className="btn-primary"
              onClick={handleUpload}
              disabled={!file || loading}
            >
              {loading ? 'Uploading...' : 'Submit to the void'}
            </button>
          </div>

          <div style={{
            background: 'rgba(255,214,10,0.06)',
            border: '1px solid rgba(255,214,10,0.2)',
            borderRadius: 'var(--radius)',
            padding: '12px 16px',
            marginTop: 20,
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            lineHeight: 1.7,
          }}>
            Your photo enters a moderation queue. Once approved it goes live.
            You can start rating others immediately while you wait.
          </div>
        </div>
      </div>
    </div>
  );
}
